import type { HydratedCustomSection } from '@/features/customization/schema/custom-section.schema';
import { CUSTOM_SECTION_ENCOUNTER_TYPE_UUID } from '@/features/prescription/custom-section/constant';
import type { CustomSectionPrintData } from '@/features/prescription/custom-section/custom-section-print-store';
import type { HistoryFormConfig } from '@/features/prescription/history-of-present-illness/utils';
import { EMPTY_RESULT } from '@/shared/constants';
import type { PrescriptionData } from '@/shared/types/prescription/prescription';
import type { VisitEncounter } from '@/shared/types/prescription/visit';
import { hasStoredRichTextContent } from '@/shared/utils/tiptap';
import { z } from 'zod';

type SetPrescription = (updater: (previous: PrescriptionData) => PrescriptionData) => void;

export interface CustomSectionValue {
  conceptUuid: string;
  label: string;
  value: string;
}

/** Answered obs from a visit, keyed by concept. Blank rich-text docs are dropped. */
const answeredObsByConcept = (encounters: VisitEncounter[] | undefined) => {
  const values = new Map<string, string>();

  for (const encounter of encounters ?? []) {
    for (const obs of encounter?.obs ?? []) {
      const conceptUuid = obs?.concept?.uuid;
      if (!conceptUuid || obs?.value == null) continue;

      // An untouched field is stored as EMPTY_RESULT, which would otherwise read as an answer.
      const value = String(obs.value);
      if (value !== EMPTY_RESULT && hasStoredRichTextContent(value)) values.set(conceptUuid, value);
    }
  }

  return values;
};

/**
 * A section's answered fields from one visit. Custom sections share an encounter type,
 * so fields are matched by concept. Labels come from the section, never from the obs —
 * those carry the fully-specified concept name.
 */
export const getCustomSectionValues = (
  encounters: VisitEncounter[] | undefined,
  section: HydratedCustomSection,
): CustomSectionValue[] => {
  const values = answeredObsByConcept(encounters);

  return [...section.fields]
    .sort((a, b) => a.order - b.order)
    .flatMap(field => {
      const value = values.get(field.conceptUuid);
      return value ? [{ conceptUuid: field.conceptUuid, label: field.label, value }] : [];
    });
};

/** Concepts answered in any visit, so many sections can be tested without re-walking the obs. */
export const getAnsweredConceptUuids = (encountersByVisit: Record<string, VisitEncounter[]> | undefined) =>
  Object.values(encountersByVisit ?? {}).reduce<Set<string>>((answered, encounters) => {
    answeredObsByConcept(encounters).forEach((_, conceptUuid) => answered.add(conceptUuid));
    return answered;
  }, new Set());

/** Tailwind needs literal class names, so grid widths are mapped rather than interpolated. */
const GRID_COLUMN_CLASS: Record<number, string> = {
  2: 'grid grid-cols-2 gap-x-4',
  3: 'grid grid-cols-3 gap-x-4',
  4: 'grid grid-cols-4 gap-x-4',
};

const layoutClassName = (section: HydratedCustomSection) => {
  if (section.displayFormat !== 'grid') return undefined;
  return GRID_COLUMN_CLASS[section.columns ?? 2] ?? GRID_COLUMN_CLASS[2];
};

/**
 * Builds the config the shared history form renders from.
 *
 * `conceptKey` is the field's concept uuid — `useCustomSectionService` keys its
 * maps the same way, so renaming a field cannot break the lookup.
 */
export const buildCustomSectionFormConfig = (section: HydratedCustomSection): HistoryFormConfig => {
  const orderedFields = [...section.fields].sort((a, b) => a.order - b.order);

  const fields = orderedFields.map(field => ({
    name: field.name,
    label: field.label,
    placeholder: `Enter ${field.label.toLowerCase()}`,
    elementId: `custom-section-${field.conceptUuid}`,
    testId: `custom-section-${field.conceptUuid}`,
    rows: 4,
    conceptKey: field.conceptUuid,
  }));

  // Text fields only for now, so every field is an optional string.
  const schema = z.object(
    Object.fromEntries(fields.map(field => [field.name, z.string().optional()])) as Record<
      string,
      z.ZodOptional<z.ZodString>
    >,
  );

  const syncPrintStore = (formData: Record<string, string>, setPrescription: SetPrescription) => {
    const printData: CustomSectionPrintData = {
      label: section.label,
      fields: orderedFields.map(field => ({
        label: field.label,
        value: formData[field.name] ?? '',
      })),
    };

    setPrescription(prev => ({
      ...prev,
      customSections: { ...prev?.customSections, [section.conceptUuid]: printData },
    }));
  };

  return {
    fields,
    concept: section.conceptUuid,
    encounterType: CUSTOM_SECTION_ENCOUNTER_TYPE_UUID,
    title: section.label,
    schema,
    className: layoutClassName(section),
    customSyncFormToStore: syncPrintStore,
    useRichText: true,
  };
};
